/**
 * Composite renderer: WebGL points + Canvas2D (DESIGNS-TRACK-3D / X §6).
 *
 * - `#stage` (constructor canvas): always Canvas2D — full draw OR lens overlay + hits
 * - sibling `.stage-gl`: WebGL2 points when selected; hidden in pure Canvas2D mode
 * Projection is shared via camera.ts so dim=3 orbit works on both paths.
 */

import type { SimView } from "../core/simView";
import { lensRegistry, loadBuiltinPlugins } from "../plugins";
import type { DrawContext } from "../plugins/types";
import type { RenderParams } from "../sim/params";
import { CanvasRenderer } from "./canvas2d";
import type { Renderer } from "./types";
import { tryCreateWebGL2Renderer, type WebGL2Renderer } from "./webgl2";

loadBuiltinPlugins();

export const WEBGL_N_THRESHOLD = 3000;

export function shouldUseWebGL(
  n: number,
  backend: RenderParams["backend"],
): boolean {
  if (backend === "canvas") return false;
  if (backend === "webgl") return true;
  return n >= WEBGL_N_THRESHOLD;
}

export class CompositeRenderer implements Renderer {
  readonly canvas: HTMLCanvasElement;
  lastDrawMs = 0;
  screenX = new Float64Array(0);
  screenY = new Float64Array(0);

  private readonly canvas2d: CanvasRenderer;
  private glCanvas: HTMLCanvasElement | null = null;
  private webgl: WebGL2Renderer | null = null;
  private webglFailed = false;
  private container: Element | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.canvas2d = new CanvasRenderer(canvas);
  }

  observe(container: Element): void {
    // Prefer .stage-host (sibling of transport); never size against .stage-wrap.
    const host =
      (this.canvas.parentElement as HTMLElement | null) ??
      (container.querySelector?.(".stage-host") as HTMLElement | null) ??
      (container as HTMLElement);
    this.container = host;
    if (getComputedStyle(host).position === "static") {
      host.style.position = "relative";
    }
    this.canvas.style.position = "relative";
    this.canvas.style.zIndex = "1";
    this.canvas2d.observe(host);
    if (this.webgl) this.webgl.observe(host);
  }

  disconnect(): void {
    this.canvas2d.disconnect();
    this.webgl?.disconnect();
    if (this.glCanvas?.parentElement) {
      this.glCanvas.parentElement.removeChild(this.glCanvas);
    }
    this.glCanvas = null;
    this.webgl = null;
  }

  clear(color: string): void {
    this.canvas2d.clear(color);
    this.webgl?.clear(color);
  }

  draw(view: SimView, render: RenderParams): void {
    const wantGl = shouldUseWebGL(view.n, render.backend) && !this.webglFailed;

    if (wantGl) {
      if (!this.ensureWebgl()) {
        // fall through
      } else {
        this.showGl(true);
        this.webgl!.draw(view, render);
        this.canvas2d.project(view, render);
        this.screenX = this.canvas2d.screenX;
        this.screenY = this.canvas2d.screenY;
        this.drawLensesOverlay(view, render);
        this.lastDrawMs = this.webgl!.lastDrawMs ?? 0;
        return;
      }
    }

    this.showGl(false);
    this.canvas2d.draw(view, render);
    this.screenX = this.canvas2d.screenX;
    this.screenY = this.canvas2d.screenY;
    this.lastDrawMs = this.canvas2d.lastDrawMs ?? 0;
  }

  private ensureWebgl(): boolean {
    if (this.webgl) return true;
    if (!this.container) return false;
    // Host must be .stage-host (or canvas parent) — not .stage-wrap — so absolute
    // WebGL does not paint over the playback transport toolbar.
    const host = (this.canvas.parentElement ?? this.container) as HTMLElement;
    const glc = document.createElement("canvas");
    glc.className = "stage-gl";
    glc.setAttribute("aria-hidden", "true");
    host.insertBefore(glc, this.canvas);
    const gl = tryCreateWebGL2Renderer(glc);
    if (!gl) {
      host.removeChild(glc);
      this.webglFailed = true;
      return false;
    }
    this.glCanvas = glc;
    this.webgl = gl;
    gl.observe(host);
    return true;
  }

  private showGl(on: boolean): void {
    if (this.glCanvas) this.glCanvas.style.display = on ? "block" : "none";
  }

  private drawLensesOverlay(view: SimView, render: RenderParams): void {
    const ctx = this.canvas.getContext("2d");
    if (!ctx) return;
    const cssW = Math.max(1, this.canvas.clientWidth || 1);
    const cssH = Math.max(1, this.canvas.clientHeight || 1);
    ctx.clearRect(0, 0, cssW, cssH);

    // C8: gate only via lens registry — no hardcoded flag OR-list
    const lenses = lensRegistry
      .list()
      .filter((lens) => lens.always || lens.enabled(render));
    if (lenses.length === 0) return;

    const range = render.plotRange;
    const scale = Math.min(cssW, cssH) / (2 * range);
    const g: DrawContext = {
      ctx,
      width: cssW,
      height: cssH,
      scale,
      cx: cssW / 2,
      cy: cssH / 2,
      render,
      screenX: this.canvas2d.screenX,
      screenY: this.canvas2d.screenY,
      depth: this.canvas2d.depth,
    };
    for (const lens of lenses) {
      lens.draw(view, g);
    }
  }
}
